/**
 * postMessage Bridge — MetaDefender Cloud (iframe) → My OPSWAT (parent)
 *
 * All events share a typed envelope so the parent can safely identify and
 * route them without ambiguity with other postMessage senders.
 *
 * Dispatch rules
 * ──────────────
 * • navigation events  — always sent when embedded (route changes are state,
 *                        not interactions, so they bypass the active gate)
 * • analytics events   — only sent when the iframe is considered "active"
 *                        (the user has focused or hovered into the iframe)
 * • lifecycle events   — always sent so the parent can track the active/
 *                        inactive transition correctly
 *
 * Target origin
 * ─────────────
 * Set GATSBY_PARENT_ORIGIN to the My OPSWAT origin (e.g. https://my.opswat.com).
 * In non-production builds it falls back to '*' with a console warning.
 */

// ─── Public types ─────────────────────────────────────────────────────────────

export type EventType = 'navigation' | 'analytics' | 'lifecycle';

export interface IframeEvent {
    /** Discriminator — lets the parent filter out unrelated postMessage senders. */
    source: 'metadefender-cloud';
    /** Schema version — bump when the envelope shape changes. */
    version: '1';
    type: EventType;
    payload: {
        /** Mirrors the GTM event name where applicable. */
        event: string;
        [key: string]: unknown;
    };
    meta: {
        /** ISO-8601 timestamp produced inside the iframe. */
        timestamp: string;
        /** Current iframe path + search (no origin — avoids leaking sensitive query params unintentionally). */
        url: string;
        /**
         * Stable per-page-load ID.  The parent uses this to correlate a burst of
         * events to a single iframe session and to deduplicate retries.
         */
        sessionId: string;
    };
}

// ─── Internal state ───────────────────────────────────────────────────────────

let _isActive = false;
let _sessionId: string | null = null;
let _listenersRegistered = false;
let _targetOriginWarned = false;

/**
 * Reset all internal module state.
 * Only intended for unit tests — do not call in application code.
 * @internal
 */
export const __resetStateForTesting = (): void => {
    _isActive = false;
    _sessionId = null;
    _listenersRegistered = false;
    _targetOriginWarned = false;
};

// ─── Environment helpers ──────────────────────────────────────────────────────

/** True when the page is rendered inside an iframe. */
export const isEmbedded = (): boolean => {
    if (typeof window === 'undefined') return false;
    try {
        return window.self !== window.top;
    } catch {
        // Cross-origin access to window.top throws — we ARE in an iframe.
        return true;
    }
};

/** Resolved target origin for postMessage calls. */
const getTargetOrigin = (): string => {
    const configured = process.env.GATSBY_PARENT_ORIGIN;
    if (configured) return configured;

    if (!_targetOriginWarned) {
        _targetOriginWarned = true;
        if (process.env.GATSBY_ENV === 'prod') {
            console.error(
                '[postMessageBridge] GATSBY_PARENT_ORIGIN must be set in production. ' +
                'Refusing to postMessage with origin "*" in prod.'
            );
            return 'null'; // will cause postMessage to fail silently — intentional
        }
        console.warn(
            '[postMessageBridge] GATSBY_PARENT_ORIGIN is not set. ' +
            'Using "*" for local development only. Set this env var before deploying.'
        );
    }
    return '*';
};

/** Stable per-load session identifier. */
const getSessionId = (): string => {
    if (!_sessionId) {
        _sessionId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : Math.random().toString(36).slice(2) + Date.now().toString(36);
    }
    return _sessionId;
};

// ─── Activity tracking ────────────────────────────────────────────────────────

const setActive = (active: boolean): void => {
    if (_isActive === active) return;
    _isActive = active;
    // Notify the parent immediately so it can open/close its own event gate.
    dispatchToParent('lifecycle', { event: active ? 'iframe:active' : 'iframe:inactive' });
};

/**
 * Register focus / blur / pointer-entry listeners.
 * Returns a cleanup function — call it on unmount if needed.
 * Idempotent: subsequent calls are no-ops.
 */
export const initIframeActivityTracking = (): (() => void) => {
    if (typeof window === 'undefined' || !isEmbedded()) return () => undefined;
    if (_listenersRegistered) return () => undefined;

    _listenersRegistered = true;

    const onFocus = () => setActive(true);
    const onBlur = () => setActive(false);
    // Pointer entering the iframe content area counts as activation even
    // before the user clicks (so scrolling-triggered analytics still fire).
    const onPointerEnter = () => setActive(true);

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    document.addEventListener('pointerenter', onPointerEnter);

    // Treat a focused iframe at load time as immediately active.
    setActive(document.hasFocus());

    return () => {
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('blur', onBlur);
        document.removeEventListener('pointerenter', onPointerEnter);
        _listenersRegistered = false;
    };
};

// ─── Core dispatcher ──────────────────────────────────────────────────────────

/** Low-level dispatcher — bypasses the active gate. */
const dispatchToParent = (
    type: EventType,
    payload: { event: string; [key: string]: unknown }
): void => {
    const origin = getTargetOrigin();
    if (origin === 'null') return; // production safety guard

    const message: IframeEvent = {
        source: 'metadefender-cloud',
        version: '1',
        type,
        payload,
        meta: {
            timestamp: new Date().toISOString(),
            url: window.location.pathname + window.location.search,
            sessionId: getSessionId(),
        },
    };

    window.parent.postMessage(message, origin);
};

/**
 * Send a typed event to the parent window.
 *
 * No-op when:
 *   – SSR (no window)
 *   – Not running inside an iframe
 *   – The iframe is inactive AND the event type is 'analytics'
 *     (navigation and lifecycle events bypass the active gate)
 */
export const sendToParent = (
    type: EventType,
    payload: { event: string; [key: string]: unknown }
): void => {
    if (typeof window === 'undefined') return;
    if (!isEmbedded()) return;

    // Gate analytics events behind the active check; let navigation and
    // lifecycle events through unconditionally.
    if (type === 'analytics' && !_isActive) return;

    dispatchToParent(type, payload);
};
